import axios, { AxiosResponse } from 'axios';
import { formatISO } from 'date-fns';

import type {
  CreateAutoApprovalParams,
  CreateCourseRelationshipParams,
  FetchCoursesWithValidationParams,
  SendAutoApprovalRequestParams,
  UpdateAutoApprovalParams,
  UpdateCourseDataParams,
} from 'models/auto-approval';

const ax = axios.create();

const sendToServer = async ({
  baseURL,
  method,
  endpoint,
  params,
  headers,
}: SendAutoApprovalRequestParams): Promise<AxiosResponse> => {
  const reply = await ax.request({
    baseURL,
    method,
    headers,
    data: params,
    timeout: 30000,
    url: endpoint,
  });

  return reply;
};

const token = localStorage.getItem('tokenIES');
const baseURL = process.env.REACT_APP_LEGADO_API as string;
const headers = {
  Authorization: token,
};

const fetchAllAutoApprovals = async (): Promise<any> => {
  const method = 'get';
  const endpoint = '/aprovacoes-automatizadas';

  const response = await sendToServer({
    baseURL,
    method,
    endpoint,
    headers,
  });

  return response;
};

const fetchCoursesWithValidation = async ({
  bolsaID,
  aprovacaoID,
}: FetchCoursesWithValidationParams): Promise<any> => {
  const method = 'get';
  const endpoint = `/bolsas-curso/${bolsaID}/aprovacao-automatica/${aprovacaoID}`;

  const response = sendToServer({
    baseURL,
    method,
    endpoint,
    headers,
  });

  return response;
};

const createAutoApprovalService = async (
  data: CreateAutoApprovalParams,
): Promise<any> => {
  const { bolsa, nome, periodo, prazoFinal, prazoInicial, status, tipo } = data;

  const method = 'post';

  const endpoint = '/aprovacoes-automatizadas';

  const response = sendToServer({
    baseURL,
    method,
    endpoint,
    headers,
    params: {
      bolsa,
      nome,
      periodo,
      prazoFinal,
      prazoInicial,
      status,
      tipo,
    },
  });

  return response;
};

const createCourseRelationship = async (
  data: CreateCourseRelationshipParams[],
): Promise<any> => {
  const method = 'post';

  const endpoint = '/aprovacoes-automatizadas-cursos';

  const response = sendToServer({
    baseURL,
    method,
    endpoint,
    headers,
    params: data,
  });

  return response;
};

const fetchAutoApprovalDetail = async (id: number): Promise<any> => {
  const method = 'get';
  const endpoint = `/aprovacoes-automatizadas/${id}`;

  const response = sendToServer({
    baseURL,
    endpoint,
    method,
    headers,
  });

  return response;
};

const fetchBolsasMaisAcesso = async (): Promise<any> => {
  const method = 'get';
  const endpoint = '/bolsas/mais-acesso';

  const response = sendToServer({
    baseURL,
    endpoint,
    method,
    headers,
  });

  return response;
};

const deleteCourseRelationship = async (id: number): Promise<any> => {
  const method = 'delete';
  const endpoint = `/aprovacoes-automatizadas-cursos/${id}`;

  const response = sendToServer({
    baseURL,
    endpoint,
    method,
    headers,
  });

  return response;
};

const deleteAutoApproval = async (id: number): Promise<any> => {
  const method = 'delete';
  const endpoint = `/aprovacoes-automatizadas/${id}`;

  const response = sendToServer({
    baseURL,
    endpoint,
    method,
    headers,
  });

  return response;
};

const updateCourseData = async (
  params: UpdateCourseDataParams,
): Promise<any> => {
  const method = 'patch';

  const endpoint = '/aprovacoes-automatizadas-cursos';

  const response = sendToServer({
    baseURL,
    endpoint,
    method,
    headers,
    params,
  });

  return response;
};

const updateAutoApprovalStatus = async (id: number): Promise<any> => {
  const method = 'patch';
  const endpoint = `/aprovacoes-automatizadas/${id}/status`;

  const response = sendToServer({
    baseURL,
    endpoint,
    method,
    headers,
    params: {
      url: window.location.href,
    },
  });

  return response;
};

const updateAutoApprovalData = async (
  params: UpdateAutoApprovalParams,
): Promise<any> => {
  const method = 'put';
  const endpoint = '/aprovacoes-automatizadas';

  const response = sendToServer({
    baseURL,
    endpoint,
    method,
    headers,
    params: {
      ...params,
      prazoInicial: formatISO(params.prazoInicial),
      prazoFinal: formatISO(params.prazoFinal),
    },
  });

  return response;
};

export {
  fetchAllAutoApprovals,
  fetchCoursesWithValidation,
  createCourseRelationship,
  createAutoApprovalService,
  deleteAutoApproval,
  fetchAutoApprovalDetail,
  fetchBolsasMaisAcesso,
  deleteCourseRelationship,
  updateAutoApprovalStatus,
  updateCourseData,
  updateAutoApprovalData,
};
